/**
 * Versioned chat message parts for Unify AI assistant UIs.
 * Provider-neutral; validated server-side before reaching the client.
 */

export const CHAT_ASSISTANT_RESPONSE_SCHEMA_VERSION = 1 as const;

export interface ChatTextPart {
  type: 'text';
  markdown: string;
}

export interface ChatChecklistOption {
  id: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface ChatChecklistPart {
  type: 'checklist';
  id: string;
  prompt: string;
  options: ChatChecklistOption[];
  selectionMode: 'single' | 'multiple';
  minSelections?: number;
  maxSelections?: number;
  allowOther?: boolean;
  otherLabel?: string;
  allowSkip?: boolean;
  submitLabel?: string;
  progress?: { current: number; total: number };
  /**
   * When set to `symptoms`, selected labels (and Other text) become
   * provider-confirmed EvidenceGroundedSymptom entries for generation.
   */
  clinicalField?: TreatmentPlanChecklistClinicalField;
}

/** Machine-readable purpose for checklist submissions that feed clinical evidence. */
export type TreatmentPlanChecklistClinicalField =
  | 'symptoms'
  | 'goals'
  | 'objectives'
  | 'interventions'
  | 'other';

export interface ChatTableColumn {
  id: string;
  header: string;
  type?: 'text' | 'number' | 'date';
}

export interface ChatTablePart {
  type: 'table';
  id: string;
  title?: string;
  columns: ChatTableColumn[];
  rows: Array<Record<string, string | number | null>>;
  /** Optional DocumentReference id for large datasets. */
  documentReferenceId?: string;
}

export type SafeChartType = 'line' | 'bar' | 'stacked-bar' | 'scatter';

export interface SafeChartSpec {
  chartType: SafeChartType;
  title?: string;
  xField: string;
  yField?: string;
  seriesField?: string;
  units?: string;
  labels?: Record<string, string>;
  data: Array<Record<string, string | number | null>>;
}

export interface ChatChartPart {
  type: 'chart';
  id: string;
  spec: SafeChartSpec;
}

export interface ChatImagePart {
  type: 'image';
  id: string;
  /** DocumentReference id — never an arbitrary public URL. */
  documentReferenceId: string;
  alt: string;
  contentType?: string;
}

export type ChatMessagePart =
  | ChatTextPart
  | ChatChecklistPart
  | ChatTablePart
  | ChatChartPart
  | ChatImagePart;

export interface ChatAssistantResponse {
  schemaVersion: typeof CHAT_ASSISTANT_RESPONSE_SCHEMA_VERSION;
  parts: ChatMessagePart[];
}

export interface ChatChecklistSubmission {
  checklistId: string;
  selectedOptionIds: string[];
  selectedLabels: string[];
  otherText?: string;
  skipped?: boolean;
}

/** Adapt legacy suggestedResponses string chips into a checklist part. */
export function adaptLegacySuggestedResponsesToChecklist(
  suggestedResponses: string[] | undefined,
  prompt = 'Choose a reply',
): ChatChecklistPart | undefined {
  if (!suggestedResponses?.length) return undefined;
  return {
    type: 'checklist',
    id: `legacy-${Date.now()}`,
    prompt,
    selectionMode: 'multiple',
    options: suggestedResponses.map((label, index) => ({
      id: `opt-${index}`,
      label,
    })),
    allowOther: true,
    otherLabel: 'Something else',
    submitLabel: 'Continue',
  };
}

const ALLOWED_PART_TYPES = new Set(['text', 'checklist', 'table', 'chart', 'image']);

export function validateChatAssistantResponse(raw: unknown): ChatAssistantResponse {
  if (!raw || typeof raw !== 'object') {
    throw new Error('ChatAssistantResponse must be an object');
  }
  const candidate = raw as Partial<ChatAssistantResponse>;
  if (candidate.schemaVersion !== 1) {
    throw new Error('Unsupported ChatAssistantResponse schemaVersion');
  }
  if (!Array.isArray(candidate.parts) || candidate.parts.length === 0) {
    throw new Error('ChatAssistantResponse.parts must be a non-empty array');
  }
  for (const part of candidate.parts) {
    if (!part || typeof part !== 'object' || !('type' in part)) {
      throw new Error('Invalid chat message part');
    }
    if (!ALLOWED_PART_TYPES.has((part as { type: string }).type)) {
      throw new Error(`Unknown chat message part type: ${String((part as { type: unknown }).type)}`);
    }
  }
  return candidate as ChatAssistantResponse;
}
